/**
 * PWA-optimized token storage
 * Uses localStorage with offline-first approach for reliability in installed PWA
 */

export const PWA_TOKEN_KEY = "pwa_auth_tokens";
export const PWA_TOKEN_SYNC_KEY = "pwa_token_sync_marker";

interface PWATokenStore {
  accessToken: string;
  refreshToken: string;
  gluceraAccessToken: string;
  gluceraRefreshToken: string;
  lastSyncTime: number;
}

/**
 * Save all tokens to IndexedDB as backup (PWA reliability)
 * Falls back to localStorage if IndexedDB not available
 */
export async function savePWATokensReliably(tokens: {
  access: string;
  refresh: string;
  gluceraAccess?: string;
  gluceraRefresh?: string;
}): Promise<void> {
  const store: PWATokenStore = {
    accessToken: tokens.access,
    refreshToken: tokens.refresh,
    gluceraAccessToken: tokens.gluceraAccess || tokens.access,
    gluceraRefreshToken: tokens.gluceraRefresh || tokens.refresh,
    lastSyncTime: Date.now(),
  };

  try {
    // Try IndexedDB first (more reliable for PWA)
    if ("indexedDB" in window) {
      const dbRequest = indexedDB.open("glucera_auth", 1);

      dbRequest.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains("tokens")) {
          db.createObjectStore("tokens", { keyPath: "id" });
        }
      };

      dbRequest.onsuccess = (event: Event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = db.transaction(["tokens"], "readwrite");
        const objectStore = transaction.objectStore("tokens");
        // Persist the actual token payload (previously spread the IDB store
        // object by mistake, so the IndexedDB backup never held real tokens).
        objectStore.put({ id: 1, ...store });
      };

      dbRequest.onerror = () => {
        // Fallback: Save to localStorage if IndexedDB fails
        localStorage.setItem(PWA_TOKEN_KEY, JSON.stringify(store));
      };
    } else {
      // Fallback to localStorage
      localStorage.setItem(PWA_TOKEN_KEY, JSON.stringify(store));
    }

    // Also update localStorage directly for compatibility
    localStorage.setItem("accessToken", tokens.access);
    localStorage.setItem("refreshToken", tokens.refresh);
    localStorage.setItem("glucera_access_token", tokens.gluceraAccess || tokens.access);
    localStorage.setItem("glucera_refresh_token", tokens.gluceraRefresh || tokens.refresh);
    
    // Mark as synced to prevent re-restore loops
    localStorage.setItem(PWA_TOKEN_SYNC_KEY, Date.now().toString());
  } catch (error) {
    console.error("❌ Error saving PWA tokens:", error);
  }
}

/**
 * Restore tokens from IndexedDB or localStorage for offline PWA
 */
export async function restorePWATokensIfNeeded(): Promise<PWATokenStore | null> {
  try {
    // Check if tokens were recently synced
    const lastSync = localStorage.getItem(PWA_TOKEN_SYNC_KEY);
    if (lastSync) {
      const syncTime = parseInt(lastSync, 10);
      // If synced within last 5 minutes, don't restore (avoid resets)
      if (Date.now() - syncTime < 5 * 60 * 1000) {
        return null;
      }
    }

    // Try to restore from localStorage first (fastest)
    const storedTokens = localStorage.getItem(PWA_TOKEN_KEY);
    if (storedTokens) {
      try {
        const tokens = JSON.parse(storedTokens) as PWATokenStore;
        console.log("✅ Restored PWA tokens from localStorage");
        return tokens;
      } catch {
        // Invalid JSON, try IndexedDB
      }
    }

    // Try IndexedDB with timeout (slower)
    if ("indexedDB" in window) {
      return new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
          console.warn("⏱️ IndexedDB restore timeout - using fallback");
          resolve(null);
        }, 1000); // 1 second timeout for IndexedDB

        try {
          const dbRequest = indexedDB.open("glucera_auth", 1);

          dbRequest.onsuccess = (event: Event) => {
            clearTimeout(timeoutId);
            const db = (event.target as IDBOpenDBRequest).result;
            const transaction = db.transaction(["tokens"], "readonly");
            const store = transaction.objectStore("tokens");
            const getRequest = store.get(1);

            getRequest.onsuccess = () => {
              if (getRequest.result) {
                const { id, ...tokens } = getRequest.result;
                console.log("✅ Restored PWA tokens from IndexedDB");
                resolve(tokens as PWATokenStore);
              } else {
                resolve(null);
              }
            };

            getRequest.onerror = () => resolve(null);
          };

          dbRequest.onerror = () => {
            clearTimeout(timeoutId);
            resolve(null);
          };
        } catch {
          clearTimeout(timeoutId);
          resolve(null);
        }
      });
    }

    return null;
  } catch (error) {
    console.error("❌ Error restoring PWA tokens:", error);
    return null;
  }
}

/**
 * Clear all PWA token storage
 */
export async function clearPWATokenStorage(): Promise<void> {
  try {
    // Clear localStorage
    localStorage.removeItem(PWA_TOKEN_KEY);
    localStorage.removeItem(PWA_TOKEN_SYNC_KEY);

    // Clear IndexedDB
    if ("indexedDB" in window) {
      const dbRequest = indexedDB.deleteDatabase("glucera_auth");
      dbRequest.onsuccess = () => {
        console.log("✅ Cleared PWA IndexedDB");
      };
      dbRequest.onerror = () => {
        console.error("❌ Error clearing PWA IndexedDB");
      };
    }
  } catch (error) {
    console.error("❌ Error clearing PWA token storage:", error);
  }
}

/**
 * Check if PWA app is online
 */
export function isPWAOnline(): boolean {
  return navigator.onLine;
}

/**
 * Listen for online/offline events in PWA
 */
export function setupPWAConnectivityListener(
  onOnline?: () => void,
  onOffline?: () => void
): () => void {
  const handleOnline = () => {
    console.log("📱 PWA is now online");
    onOnline?.();
  };

  const handleOffline = () => {
    console.log("📴 PWA is now offline");
    onOffline?.();
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
}
